import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import adminService, {
    BusinessDocument,
    CreateBusinessDocumentRequest,
} from '../../services/admin/adminService';

const BusinessDocumentsManagement = () => {
    const [documents, setDocuments] = useState<BusinessDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchDebounce, setSearchDebounce] = useState('');

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<BusinessDocument | null>(null);

    // Form states
    const [formData, setFormData] = useState<CreateBusinessDocumentRequest>({
        document_name: '',
        document_type: '',
        document_id: '',
        document_url: '',
    });

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery);
            setCurrentPage(1); // Reset to first page on new search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch documents
    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const response = await adminService.getBusinessDocuments({
                page: currentPage,
                limit: perPage,
                search: searchDebounce || undefined,
            });
            setDocuments(response.documents);
            setTotalPages(response.pagination.total_pages);
            setTotalDocuments(response.pagination.total_documents);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch documents');
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, searchDebounce]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Handle create document
    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await adminService.createBusinessDocument(formData);
            toast.success('Document created successfully');
            setShowCreateModal(false);
            resetForm();
            fetchDocuments();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to create document');
        }
    };

    // Handle update document
    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDocument) return;

        try {
            await adminService.updateBusinessDocument({
                id: selectedDocument.id,
                document_name: formData.document_name || undefined,
                document_type: formData.document_type || undefined,
                document_id: formData.document_id || undefined,
                document_url: formData.document_url || undefined,
            });
            toast.success('Document updated successfully');
            setShowEditModal(false);
            resetForm();
            setSelectedDocument(null);
            fetchDocuments();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update document');
        }
    };

    // Handle delete document
    const handleDelete = async () => {
        if (!selectedDocument) return;

        try {
            await adminService.deleteBusinessDocument(selectedDocument.id);
            toast.success('Document deleted successfully');
            setShowDeleteModal(false);
            setSelectedDocument(null);
            fetchDocuments();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete document');
        }
    };

    const resetForm = () => {
        setFormData({
            document_name: '',
            document_type: '',
            document_id: '',
            document_url: '',
        });
    };

    const openEditModal = (document: BusinessDocument) => {
        setSelectedDocument(document);
        setFormData({
            document_name: document.document_name,
            document_type: document.document_type,
            document_id: document.document_id,
            document_url: document.document_url,
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (document: BusinessDocument) => {
        setSelectedDocument(document);
        setShowDeleteModal(true);
    };

    const openViewModal = (document: BusinessDocument) => {
        setSelectedDocument(document);
        setShowViewModal(true);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Business Documents Management</h1>
                        <p className="text-gray-600 mt-1 text-sm md:text-base">Manage business document types for verification and compliance</p>
                    </div>
                </div>

                {/* Search and Actions */}
                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-center">
                        <div className="lg:col-span-6 relative">
                            <input
                                type="text"
                                placeholder="Search documents..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="lg:col-span-3">
                            <select
                                value={perPage}
                                onChange={(e) => {
                                    setPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={10}>10 per page</option>
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        </div>
                        <div className="lg:col-span-3">
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                            >
                                + Add Document
                            </button>
                        </div>
                    </div>
                </div>

                {/* Documents Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Document Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Document Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Document ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created Date
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                            Loading documents...
                                        </td>
                                    </tr>
                                ) : documents.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                            {searchDebounce ? 'No documents found matching your search' : 'No documents available'}
                                        </td>
                                    </tr>
                                ) : (
                                    documents.map((doc) => (
                                        <tr key={doc.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{doc.document_name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{doc.document_type}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">{doc.document_id}</code>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{formatDate(doc.created_at)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => openViewModal(doc)}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => openEditModal(doc)}
                                                    className="text-green-600 hover:text-green-900 mr-4"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal(doc)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="text-sm text-gray-700 order-2 md:order-1 text-center md:text-left">
                                Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(currentPage * perPage, totalDocuments)}</span> of{' '}
                                <span className="font-medium">{totalDocuments}</span> documents
                            </div>
                            <div className="flex items-center gap-2 order-1 md:order-2">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                                >
                                    Previous
                                </button>
                                <span className="px-3 md:px-4 py-2 text-xs md:text-sm text-gray-700 whitespace-nowrap">
                                    Page {currentPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h2 className="text-2xl font-bold mb-4">Add New Document</h2>
                        <form onSubmit={handleCreate}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.document_name}
                                        onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="e.g., GST Certificate"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.document_type}
                                        onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="e.g., GST"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document ID *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.document_id}
                                        onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="e.g., gst_certificate"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document URL (Optional)</label>
                                    <input
                                        type="url"
                                        value={formData.document_url}
                                        onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="https://example.com/template"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Create Document
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedDocument && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h2 className="text-2xl font-bold mb-4">Edit Document</h2>
                        <form onSubmit={handleUpdate}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
                                    <input
                                        type="text"
                                        value={formData.document_name}
                                        onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                                    <input
                                        type="text"
                                        value={formData.document_type}
                                        onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document ID</label>
                                    <input
                                        type="text"
                                        value={formData.document_id}
                                        onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document URL</label>
                                    <input
                                        type="url"
                                        value={formData.document_url}
                                        onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedDocument(null);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    Update Document
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && selectedDocument && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h2 className="text-2xl font-bold mb-4 text-red-600">Delete Document</h2>
                        <p className="text-gray-700 mb-6">
                            Are you sure you want to delete <strong>{selectedDocument.document_name}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedDocument(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && selectedDocument && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h2 className="text-2xl font-bold mb-4">Document Details</h2>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Document Name</label>
                                <p className="text-gray-900 font-medium">{selectedDocument.document_name}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Document Type</label>
                                <p className="text-gray-900">{selectedDocument.document_type}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Document ID</label>
                                <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">{selectedDocument.document_id}</code>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Document URL</label>
                                {selectedDocument.document_url ? (
                                    <a
                                        href={selectedDocument.document_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline break-all"
                                    >
                                        {selectedDocument.document_url}
                                    </a>
                                ) : (
                                    <p className="text-gray-400">Not provided</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Created At</label>
                                <p className="text-gray-900">{formatDate(selectedDocument.created_at)}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Updated At</label>
                                <p className="text-gray-900">{formatDate(selectedDocument.updated_at)}</p>
                            </div>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    setSelectedDocument(null);
                                }}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessDocumentsManagement;
